import React from 'react';
import { Table as ReactTable, Row, flexRender } from '@tanstack/react-table';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import i18n from '@/utils/i18n';
import { Checkbox } from '@/components/ui/checkbox';

interface DataTableRowCardsProps<TData> {
  table: ReactTable<TData>;
  cardTitleColumnId: string;
}

interface DataTableRowCardProps<TData> {
  table: ReactTable<TData>;
  row: Row<TData>;
  cardTitleColumnId: string;
}

export function DataTableRowCard<TData>({
  table,
  row,
  cardTitleColumnId,
}: DataTableRowCardProps<TData>) {
  const titleField = row
    .getVisibleCells()
    .find((cell: any) => cell.column.id === cardTitleColumnId);

  const isSelectable = table.getAllColumns().some((c) => c.id === 'select');
  const isSelected = row.getIsSelected();

  if (!titleField || row.getVisibleCells().length === 0) {
    return null;
  }

  return (
    <Card className="tw:w-full tw:max-w-2xl tw:p-0">
      <CardHeader className="tw:py-3 tw:px-4">
        <div className="tw:flex tw:items-center tw:justify-between">
          <div className="tw:flex tw:items-center tw:gap-2">
            {isSelectable && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => {
                  row.toggleSelected(checked === true ? true : false);
                }}
              />
            )}
            <span className="tw:font-semibold">
              {flexRender(
                titleField.column.columnDef.cell,
                titleField.getContext(),
              )}
            </span>
          </div>
        </div>
      </CardHeader>

      <Separator />

      <CardContent className="tw:p-0 tw:px-4 tw:pb-1">
        {row
          .getVisibleCells()
          .filter((cell) => cell.column.id !== cardTitleColumnId)
          .map((cell, index, arr) => {
            const columnName =
              // @ts-ignore
              cell.column.columnDef.meta?.getName?.() || cell.column.id;
            const isLast = index === arr.length - 1;
            return (
              <React.Fragment key={cell.id}>
                <div className="tw:grid tw:grid-cols-2 tw:gap-2 tw:py-2">
                  <div>
                    <span className="tw:text-muted-foreground tw:text-sm">
                      {columnName}
                    </span>
                  </div>
                  <div>
                    <span className="tw:font-normal tw:flex">
                      {cell.getValue() as string}
                    </span>
                  </div>
                </div>
                {!isLast && <Separator />}
              </React.Fragment>
            );
          })}
      </CardContent>
    </Card>
  );
}

export function DataTableRowCards<TData>({
  table,
  cardTitleColumnId,
}: DataTableRowCardsProps<TData>) {
  const rows = table.getRowModel().rows;

  if (rows.length === 0) {
    return (
      <div className="tw:h-12 tw:flex tw:items-center tw:justify-center tw:text-center tw:p-4">
        {i18n.t('ItemLocation:noResults')}
      </div>
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      {rows.map((row) => (
        <DataTableRowCard
          key={row.id}
          row={row}
          table={table}
          cardTitleColumnId={cardTitleColumnId}
        />
      ))}
    </div>
  );
}
